import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from './query-client';
import type { WebTask } from './tasks';

export type { SentOutreachSummary } from './outreach-rows';

/**
 * What a task's outreaches came to: who they went to, over what, and whether anyone wrote back.
 *
 * Read out of the ALREADY-LOADED task list rather than fetched. `/api/tasks` folds `sentOutreaches`
 * onto each row it returns (one bridge call for the whole list); the single-task route `/api/task/:id`
 * carries no outreach data at all, and giving it some would mean a 100-row-per-direction outreach query
 * every time a detail opens, to learn something the list already knows.
 *
 * **The deliberate consequence:** a task detail opened for a task that is NOT in the loaded list — a
 * deep link, or the home pile-up, the cases `fetchTaskById` exists for — finds nothing here and renders
 * no outreach section. That is a missing section, never a wrong one: it cannot claim "no reply" for a
 * thread it never read. Closing it properly means the detail route learning to read outreaches, which
 * is a bridge round trip per open.
 *
 * Scans every task-list scope (`tasksAllScopes`), because the detail can be opened from the
 * account-wide list or from a Space-scoped one and the row lives in whichever of those was fetched.
 */
export function useSentOutreaches(taskId: string): WebTask['sentOutreaches'] {
  const queryClient = useQueryClient();
  if (!taskId) return undefined;
  const scopes = queryClient.getQueriesData<WebTask[]>({ queryKey: queryKeys.tasksAllScopes });
  for (const [, tasks] of scopes) {
    const row = tasks?.find((t) => t.id === taskId);
    if (row?.sentOutreaches?.length) return row.sentOutreaches;
  }
  return undefined;
}
